import React, { useState, useEffect } from 'react';
import { Save, Loader, Store, Clock, Phone, Instagram, Facebook, MapPin, DollarSign, Palette, Image as ImageIcon, CalendarDays, Plus, Trash2 } from 'lucide-react';
import { api } from '../../services/api';
import './AdminUI.css';
import AdminTopbarPortal from './AdminTopbarPortal';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const ADMIN_LOCKED_FIELDS = [
  'serviceCharge', 'domain', 'smtpEnabled', 'smtpHost', 'smtpPort', 'smtpUser',
  'smtpPassword', 'smtpFromEmail', 'smtpUseSsl', 'stripePublishableKey',
  'stripeSecretKey', 'stripeWebhookSecret'
];

export default function AdminRestaurantSettings() {
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [specialHoursSaving, setSpecialHoursSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('general');
  const [form, setForm] = useState({
    name: '',
    description: '',
    phone: '',
    instagram: '',
    socialPlatform: 'instagram',
    address: '',
    postcode: '',
    banner: '',
    bannerText: '',
    deliveryTime: '',
    pickupTime: '',
    deliveryFee: '',
    minOrder: '',
    serviceCharge: '',
    scheduleInterval: '30',
    openingHours: {},
    specialHours: [],
    domain: '',
    seoTitle: '',
    metaDescription: '',
    ogTitle: '',
    ogImage: '',
    faviconUrl: '',
    // SMTP Settings
    smtpEnabled: false,
    smtpHost: '',
    smtpPort: '465',
    smtpUser: '',
    smtpPassword: '',
    smtpFromEmail: '',
    smtpUseSsl: true,
    // Branding
    primaryColor: '#f59e0b',
    secondaryColor: '#d97706',
    mapMarkerColor: '#f59e0b',
    mapRouteColor: '#f59e0b',
    mapMarkerIcon: '',
    latitude: '',
    longitude: '',
    logoUrl: '',
    defaultItemImage: 'https://static.prod-images.emergentagent.com/jobs/7c911eb4-04a0-4f34-93d4-347dbebef30b/images/359eb04caa4922f3628d2ff4ac19a1d709065db487ebeb72617559b2c5e4fa02.png',
    // Stripe
    stripePublishableKey: '',
    stripeSecretKey: '',
    stripeWebhookSecret: ''
  });
  const [geocoding, setGeocoding] = useState(false);
  const today = new Date().toLocaleDateString('en-CA');
  const [specialDraft, setSpecialDraft] = useState({
    startDate: today,
    endDate: today,
    closed: true,
    open: '16:00',
    close: '23:00',
    label: ''
  });

  const load = async () => {
    try {
      // Use admin endpoint to get full data including Stripe secret key
      const token = localStorage.getItem('mj_token');
      const BACKEND_URL = window.location.origin;
      const response = await fetch(`${BACKEND_URL}/api/restaurant/admin`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load');
      const data = await response.json();
      
      setRestaurant(data);
      setForm({
        name: data.name || '',
        description: data.description || '',
        phone: data.phone || '',
        instagram: data.instagram || '',
        socialPlatform: data.socialPlatform === 'facebook' ? 'facebook' : 'instagram',
        address: data.address || '',
        postcode: data.postcode || '',
        banner: data.banner || '',
        bannerText: data.bannerText || '',
        deliveryTime: data.deliveryTime || '',
        pickupTime: data.pickupTime || '',
        deliveryFee: data.deliveryFee?.toString() || '',
        minOrder: data.minOrder?.toString() || '',
        serviceCharge: data.serviceCharge?.toString() || '0',
        scheduleInterval: data.scheduleInterval?.toString() || '30',
        openingHours: data.openingHours || {},
        specialHours: data.specialHours || [],
        domain: data.domain || '',
        seoTitle: data.seoTitle || '',
        metaDescription: data.metaDescription || '',
        ogTitle: data.ogTitle || '',
        ogImage: data.ogImage || '',
        faviconUrl: data.faviconUrl || '',
        // SMTP Settings
        smtpEnabled: data.smtpEnabled || false,
        smtpHost: data.smtpHost || '',
        smtpPort: data.smtpPort?.toString() || '465',
        smtpUser: data.smtpUser || '',
        smtpPassword: data.smtpPassword || '',
        smtpFromEmail: data.smtpFromEmail || '',
        smtpUseSsl: data.smtpUseSsl !== undefined ? data.smtpUseSsl : true,
        // Branding
        primaryColor: data.primaryColor || '#f59e0b',
        secondaryColor: data.secondaryColor || '#d97706',
        mapMarkerColor: data.mapMarkerColor || data.primaryColor || '#f59e0b',
        mapRouteColor: data.mapRouteColor || '#f59e0b',
        mapMarkerIcon: data.mapMarkerIcon || '',
        latitude: data.latitude?.toString() || '',
        longitude: data.longitude?.toString() || '',
        logoUrl: data.logoUrl || '',
        defaultItemImage: data.defaultItemImage || 'https://static.prod-images.emergentagent.com/jobs/7c911eb4-04a0-4f34-93d4-347dbebef30b/images/359eb04caa4922f3628d2ff4ac19a1d709065db487ebeb72617559b2c5e4fa02.png',
        // Stripe
        stripePublishableKey: data.stripePublishableKey || '',
        stripeSecretKey: '',
        stripeWebhookSecret: ''
      });
    } catch (err) {
      console.error('Failed to load restaurant:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        ...form,
        deliveryFee: parseFloat(form.deliveryFee || 0),
        minOrder: parseFloat(form.minOrder || 0),
        serviceCharge: parseFloat(form.serviceCharge || 0),
        scheduleInterval: parseInt(form.scheduleInterval || 30),
        smtpPort: parseInt(form.smtpPort || 465),
        latitude: form.latitude === '' ? null : Number(form.latitude),
        longitude: form.longitude === '' ? null : Number(form.longitude)
      };
      ADMIN_LOCKED_FIELDS.forEach(field => delete payload[field]);
      const result = await api.updateRestaurant(payload);
      
      // Update form with result to ensure we have the latest data
      setRestaurant(result);
      setForm({
        name: result.name || '',
        description: result.description || '',
        phone: result.phone || '',
        instagram: result.instagram || '',
        socialPlatform: result.socialPlatform === 'facebook' ? 'facebook' : 'instagram',
        address: result.address || '',
        postcode: result.postcode || '',
        banner: result.banner || '',
        bannerText: result.bannerText || '',
        deliveryTime: result.deliveryTime || '',
        pickupTime: result.pickupTime || '',
        deliveryFee: result.deliveryFee?.toString() || '',
        minOrder: result.minOrder?.toString() || '',
        serviceCharge: result.serviceCharge?.toString() || '0',
        scheduleInterval: result.scheduleInterval?.toString() || '30',
        openingHours: result.openingHours || {},
        specialHours: result.specialHours || [],
        domain: result.domain || '',
        seoTitle: result.seoTitle || '',
        metaDescription: result.metaDescription || '',
        ogTitle: result.ogTitle || '',
        ogImage: result.ogImage || '',
        faviconUrl: result.faviconUrl || '',
        smtpEnabled: result.smtpEnabled || false,
        smtpHost: result.smtpHost || '',
        smtpPort: result.smtpPort?.toString() || '465',
        smtpUser: result.smtpUser || '',
        smtpPassword: result.smtpPassword || '',
        smtpFromEmail: result.smtpFromEmail || '',
        smtpUseSsl: result.smtpUseSsl !== undefined ? result.smtpUseSsl : true,
        primaryColor: result.primaryColor || '#f59e0b',
        secondaryColor: result.secondaryColor || '#d97706',
        mapMarkerColor: result.mapMarkerColor || result.primaryColor || '#f59e0b',
        mapRouteColor: result.mapRouteColor || '#f59e0b',
        mapMarkerIcon: result.mapMarkerIcon || '',
        latitude: result.latitude?.toString() || '',
        longitude: result.longitude?.toString() || '',
        logoUrl: result.logoUrl || '',
        defaultItemImage: result.defaultItemImage || 'https://static.prod-images.emergentagent.com/jobs/7c911eb4-04a0-4f34-93d4-347dbebef30b/images/359eb04caa4922f3628d2ff4ac19a1d709065db487ebeb72617559b2c5e4fa02.png',
        stripePublishableKey: result.stripePublishableKey || '',
        stripeSecretKey: '',
        stripeWebhookSecret: ''
      });
      
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateHours = (day, field, value) => {
    setForm(prev => ({
      ...prev,
      openingHours: {
        ...prev.openingHours,
        [day]: {
          ...prev.openingHours[day],
          [field]: value
        }
      }
    }));
  };

  const saveSpecialHours = async nextSpecialHours => {
    setSpecialHoursSaving(true);
    setError('');
    setSuccess('');
    try {
      const result = await api.updateRestaurant({ specialHours: nextSpecialHours });
      const savedSpecialHours = result.specialHours || [];
      setRestaurant(result);
      setForm(previous => ({ ...previous, specialHours: savedSpecialHours }));
      setSuccess('Special hours saved.');
      setTimeout(() => setSuccess(''), 3000);
      return true;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save special hours');
      return false;
    } finally {
      setSpecialHoursSaving(false);
    }
  };

  const addSpecialHours = async () => {
    setError('');
    if (!specialDraft.startDate || !specialDraft.endDate) {
      setError('Choose a start and end date for the special hours.');
      return;
    }
    if (specialDraft.endDate < specialDraft.startDate) {
      setError('The end date cannot be before the start date.');
      return;
    }
    const overlaps = form.specialHours.some(entry => {
      const start = entry.startDate || entry.date;
      const end = entry.endDate || entry.date;
      return specialDraft.startDate <= end && specialDraft.endDate >= start;
    });
    if (overlaps) {
      setError('That date or period already has special hours. Edit or remove the existing entry first.');
      return;
    }
    const nextSpecialHours = [
      ...form.specialHours,
      { ...specialDraft, id: `special_${Date.now()}` }
    ].sort((a, b) => a.startDate.localeCompare(b.startDate));
    if (await saveSpecialHours(nextSpecialHours)) {
      setSpecialDraft(previous => ({ ...previous, label: '' }));
    }
  };

  const removeSpecialHours = async id => {
    await saveSpecialHours(form.specialHours.filter(entry => entry.id !== id));
  };

  const generateMapLocation = async () => {
    const postcode = form.postcode.replace(/\s+/g, '');
    const addressLine = form.address.split(',')[0].trim();
    if (!addressLine || !postcode) { setError('Enter the restaurant address and postcode first.'); return; }
    setGeocoding(true); setError('');
    try {
      const result = await api.lookupPostcode(postcode);
      const normalize = value => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const wanted = normalize(addressLine);
      const exactAddress = (result.addresses || []).find(candidate => normalize(candidate.line1) === wanted);
      if (!exactAddress || exactAddress.latitude == null || exactAddress.longitude == null) {
        throw new Error('The exact restaurant address was not found. Check the address and postcode, or enter the coordinates manually.');
      }
      setForm(previous => ({
        ...previous,
        latitude: String(exactAddress.latitude),
        longitude: String(exactAddress.longitude)
      }));
      setSuccess(`Exact map location found for ${exactAddress.line1}. Save changes to publish it.`);
    } catch (err) {
      setError(err.message || 'Could not generate the map location.');
    } finally {
      setGeocoding(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className={`admin-view admin-settings admin-settings-${activeSection} space-y-4`}>
      <AdminTopbarPortal><div className="admin-topbar-controls">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Restaurant Settings</h2>
          <p className="text-gray-500 text-sm">Manage store information and opening hours</p>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 bg-gray-900 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors disabled:opacity-60">
          {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div></AdminTopbarPortal>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-3 text-sm flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full" />
          {success}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>
      )}

      <div className="admin-settings-tabs" role="tablist" aria-label="Settings sections">
        {[
          ['general', 'General'],
          ['hours', 'Opening hours'],
          ['appearance', 'Brand & content'],
          ['email', 'Email'],
          ['payments', 'Payments'],
          ['advanced', 'Advanced']
        ].map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={activeSection === id} className={activeSection === id ? 'is-active' : ''} onClick={() => setActiveSection(id)}>{label}</button>
        ))}
      </div>

      {/* Basic Info */}
      <div className="settings-panel settings-general bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Store className="settings-section-icon" />
          <h3 className="font-semibold text-gray-900">Basic Information</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="text-xs text-gray-500 mb-1.5 block">Restaurant Name</label>
            <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-gray-500 mb-1.5 block">Description</label>
            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              rows={2} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 resize-none" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5" /> Phone
            </label>
            <input type="tel" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block flex items-center gap-1.5">
              {form.socialPlatform === 'facebook'
                ? <Facebook className="w-3.5 h-3.5" />
                : <Instagram className="w-3.5 h-3.5" />}
              Instagram / Facebook Handle
            </label>
            <div className="grid grid-cols-[9rem_minmax(0,1fr)] gap-2">
              <select value={form.socialPlatform} onChange={e => setForm(p => ({ ...p, socialPlatform: e.target.value }))}
                aria-label="Social platform"
                className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm bg-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500">
                <option value="instagram">Instagram</option>
                <option value="facebook">Facebook</option>
              </select>
              <input type="text" value={form.instagram} onChange={e => setForm(p => ({ ...p, instagram: e.target.value }))}
                placeholder="@restaurant"
                className="w-full min-w-0 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Location */}
      <div className="settings-panel settings-general bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="settings-section-icon" />
          <h3 className="font-semibold text-gray-900">Location</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label className="text-xs text-gray-500 mb-1.5 block">Address</label>
            <input type="text" value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Postcode</label>
            <input type="text" value={form.postcode} onChange={e => setForm(p => ({ ...p, postcode: e.target.value.toUpperCase() }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 font-mono" />
          </div>
        </div>
        <div className="admin-location-tools">
          <div>
            <strong>Exact map location</strong>
            <span>Used for collection markers and delivery routes.</span>
          </div>
          <div className="admin-location-coordinates">
            <input type="number" step="any" aria-label="Restaurant latitude" placeholder="Latitude" value={form.latitude} onChange={e => setForm(p => ({ ...p, latitude: e.target.value }))} />
            <input type="number" step="any" aria-label="Restaurant longitude" placeholder="Longitude" value={form.longitude} onChange={e => setForm(p => ({ ...p, longitude: e.target.value }))} />
            <button type="button" onClick={generateMapLocation} disabled={geocoding}>{geocoding ? 'Generating…' : 'Generate exact location'}</button>
          </div>
        </div>
      </div>

      {/* Service Settings */}
      <div className="settings-panel settings-general bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="settings-section-icon" />
          <h3 className="font-semibold text-gray-900">Service Settings</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Default Delivery Fee (£)</label>
            <input type="number" step="0.01" value={form.deliveryFee}
              onChange={e => setForm(p => ({ ...p, deliveryFee: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Minimum Order (£)</label>
            <input type="number" step="0.01" value={form.minOrder}
              onChange={e => setForm(p => ({ ...p, minOrder: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Service Charge (£)</label>
            <input type="number" step="0.01" value={form.serviceCharge} disabled className="w-full" />
            <p className="text-xs text-gray-400 mt-1">Fixed charge added to all orders. Contact Orderkit to change it.</p>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Estimated Delivery Time</label>
            <input type="text" placeholder="e.g. 25-40 Mins" value={form.deliveryTime}
              onChange={e => setForm(p => ({ ...p, deliveryTime: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Estimated Pickup Time</label>
            <input type="text" placeholder="e.g. 10-20 Mins" value={form.pickupTime}
              onChange={e => setForm(p => ({ ...p, pickupTime: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Schedule Later Time Interval</label>
            <select value={form.scheduleInterval}
              onChange={e => setForm(p => ({ ...p, scheduleInterval: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 bg-white">
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
              <option value="60">60 minutes</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">Interval for "Schedule Later" time slots</p>
          </div>
        </div>
      </div>

      {/* Opening Hours */}
      <div className="settings-panel settings-hours bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="settings-section-icon" />
          <h3 className="font-semibold text-gray-900">Opening Hours</h3>
        </div>
        <div className="space-y-3">
          {DAYS.map(day => {
            const dayData = form.openingHours[day] || { open: '11:30', close: '22:30', closed: false };
            return (
              <div key={day} className="flex items-center gap-3 pb-3 border-b border-gray-100 last:border-0">
                <div className="w-24">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!dayData.closed}
                      onChange={e => updateHours(day, 'closed', !e.target.checked)}
                      className="w-4 h-4 rounded accent-amber-600" />
                    <span className="text-sm font-medium text-gray-700 capitalize">{day}</span>
                  </label>
                </div>
                {dayData.closed ? (
                  <span className="text-sm text-red-500 font-medium">Closed</span>
                ) : (
                  <div className="flex items-center gap-2 flex-1">
                    <input type="time" value={dayData.open || '11:30'}
                      onChange={e => updateHours(day, 'open', e.target.value)}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
                    <span className="text-gray-400 text-sm">to</span>
                    <input type="time" value={dayData.close || '22:30'}
                      onChange={e => updateHours(day, 'close', e.target.value)}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* One-off closures and amended hours */}
      <div className="settings-panel settings-hours bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-start gap-2 mb-5">
          <CalendarDays className="settings-section-icon mt-0.5" />
          <div>
            <h3 className="font-semibold text-gray-900">Special hours and holidays</h3>
            <p className="text-xs text-gray-500 mt-1">
              Override the weekly schedule for one date or a period. Permanent changes belong in Opening Hours above.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4 border border-gray-200 rounded-xl bg-gray-50">
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">From</label>
            <input type="date" value={specialDraft.startDate}
              onChange={event => setSpecialDraft(previous => ({ ...previous, startDate: event.target.value, endDate: event.target.value > previous.endDate ? event.target.value : previous.endDate }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">To</label>
            <input type="date" min={specialDraft.startDate} value={specialDraft.endDate}
              onChange={event => setSpecialDraft(previous => ({ ...previous, endDate: event.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-gray-500 mb-1.5 block">Reason or customer message <span className="text-gray-400">(optional)</span></label>
            <input type="text" maxLength={120} value={specialDraft.label}
              onChange={event => setSpecialDraft(previous => ({ ...previous, label: event.target.value }))}
              placeholder="For example: Closed for Christmas"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white" />
          </div>

          <label className="sm:col-span-2 flex items-center gap-2 cursor-pointer min-h-[42px]">
            <input type="checkbox" checked={specialDraft.closed}
              onChange={event => setSpecialDraft(previous => ({ ...previous, closed: event.target.checked }))}
              className="w-4 h-4 rounded accent-gray-900" />
            <span className="text-sm font-medium text-gray-700">Closed all day</span>
          </label>

          {!specialDraft.closed && (
            <div className="sm:col-span-2 flex items-end gap-2">
              <div className="flex-1">
                <label className="text-xs text-gray-500 mb-1.5 block">Open</label>
                <input type="time" value={specialDraft.open}
                  onChange={event => setSpecialDraft(previous => ({ ...previous, open: event.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white" />
              </div>
              <span className="text-gray-400 pb-2.5">to</span>
              <div className="flex-1">
                <label className="text-xs text-gray-500 mb-1.5 block">Close</label>
                <input type="time" value={specialDraft.close}
                  onChange={event => setSpecialDraft(previous => ({ ...previous, close: event.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white" />
              </div>
            </div>
          )}

          <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
            <button type="button" onClick={addSpecialHours} disabled={specialHoursSaving}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-black disabled:opacity-60">
              {specialHoursSaving ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {specialHoursSaving ? 'Saving...' : 'Add special hours'}
            </button>
          </div>
        </div>

        <div className="mt-4 divide-y divide-gray-100 border-t border-gray-200">
          {form.specialHours.length === 0 ? (
            <p className="py-5 text-sm text-gray-500">No special hours or holiday closures have been added.</p>
          ) : form.specialHours.map(entry => (
            <div key={entry.id} className="py-3 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">
                  {entry.startDate === entry.endDate ? entry.startDate : `${entry.startDate} to ${entry.endDate}`}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {entry.closed ? 'Closed all day' : `${entry.open}–${entry.close}`}
                  {entry.label ? ` · ${entry.label}` : ''}
                </p>
              </div>
              <button type="button" onClick={() => removeSpecialHours(entry.id)} disabled={specialHoursSaving} aria-label="Remove special hours"
                className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-200 disabled:opacity-50">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Branding */}
      <div className="settings-panel settings-appearance admin-brand-editor bg-white rounded-2xl border border-gray-200 p-5">
        <div className="admin-brand-editor-heading">
          <div className="admin-brand-editor-icon"><Palette /></div>
          <div>
            <h3 className="font-semibold text-gray-900">Brand identity</h3>
            <p className="text-xs text-gray-500 mt-1">Choose the colours and images used across your ordering experience.</p>
          </div>
        </div>

        <div className="admin-brand-section-grid">
          <section className="admin-brand-group">
            <div className="admin-brand-group-heading"><span>01</span><div><h4>Brand colours</h4><p>Core colours used for buttons, highlights and accents.</p></div></div>
            <div className="admin-brand-colour-grid">
              {[
                ['primaryColor', 'Primary colour', 'Buttons and highlights'],
                ['secondaryColor', 'Secondary colour', 'Supporting accents']
              ].map(([field, label, help]) => (
                <div className="admin-colour-field" key={field}>
                  <label>{label}</label>
                  <div><input type="color" value={form[field]} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} aria-label={`${label} picker`} /><input type="text" value={form[field]} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} className="font-mono" /></div>
                  <p>{help}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="admin-brand-group">
            <div className="admin-brand-group-heading"><span>02</span><div><h4>Map styling</h4><p>Set the pin, route and restaurant icon shown on order maps.</p></div></div>
            <div className="admin-brand-colour-grid">
              {[
                ['mapMarkerColor', 'Marker colour', 'Pickup and delivery destination pins'],
                ['mapRouteColor', 'Route colour', 'Delivery route line']
              ].map(([field, label, help]) => (
                <div className="admin-colour-field" key={field}>
                  <label>{label}</label>
                  <div><input type="color" value={form[field]} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} aria-label={`${label} picker`} /><input type="text" value={form[field]} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} className="font-mono" /></div>
                  <p>{help}</p>
                </div>
              ))}
            </div>
            <div className="admin-brand-url-field">
              <label>Map marker icon <span>Optional</span></label>
              <input type="url" placeholder="https://example.com/map-icon.png" value={form.mapMarkerIcon} onChange={e => setForm(p => ({ ...p, mapMarkerIcon: e.target.value }))} />
              <p>Square PNG, WebP or SVG displayed inside the collection marker.</p>
            </div>
          </section>

          <section className="admin-brand-group admin-brand-group-wide">
            <div className="admin-brand-group-heading"><span>03</span><div><h4>Brand imagery</h4><p>Manage the restaurant logo and fallback menu artwork.</p></div></div>
            <div className="admin-brand-image-grid">
              <div className="admin-image-url-field">
                <div className="admin-field-label"><ImageIcon /><label>Logo URL <span>Optional</span></label></div>
                <input type="url" placeholder="https://example.com/logo.png" value={form.logoUrl} onChange={e => setForm(p => ({ ...p, logoUrl: e.target.value }))} />
                <p>Also used in the Admin navigation and customer emails.</p>
              </div>
              <div className="admin-image-url-field">
                <div className="admin-field-label"><ImageIcon /><label>Default menu item image</label></div>
                <input type="url" placeholder="Image used when a menu item has no photo" value={form.defaultItemImage} onChange={e => setForm(p => ({ ...p, defaultItemImage: e.target.value }))} />
                <p>Fallback artwork for menu items without their own image.</p>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* SMTP Email Settings */}
      <div className="settings-panel settings-email bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-gray-900">Email Settings (SMTP)</h3>
            <p className="text-xs text-gray-500 mt-1">Email delivery is configured centrally by Orderkit.</p>
          </div>
          <button type="button" role="switch" aria-checked={form.smtpEnabled} disabled className={`admin-settings-switch ${form.smtpEnabled ? 'is-on' : ''}`}><span /></button>
        </div>

        <fieldset disabled className="admin-locked-fields space-y-4 pt-3 border-t border-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">SMTP Host</label>
                <input 
                  type="text" 
                  placeholder="smtp.example.com"
                  value={form.smtpHost} 
                  onChange={e => setForm(p => ({ ...p, smtpHost: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" 
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">SMTP Port</label>
                <input 
                  type="number" 
                  placeholder="465"
                  value={form.smtpPort} 
                  onChange={e => setForm(p => ({ ...p, smtpPort: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" 
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">SMTP Username</label>
                <input 
                  type="text" 
                  placeholder="user@example.com"
                  value={form.smtpUser} 
                  onChange={e => setForm(p => ({ ...p, smtpUser: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" 
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">SMTP Password</label>
                <input 
                  type="password" 
                  placeholder="••••••••"
                  value={form.smtpPassword} 
                  onChange={e => setForm(p => ({ ...p, smtpPassword: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" 
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">From Email Address</label>
                <input 
                  type="email" 
                  placeholder="noreply@example.com"
                  value={form.smtpFromEmail} 
                  onChange={e => setForm(p => ({ ...p, smtpFromEmail: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" 
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">Connection Security</label>
                <select 
                  value={form.smtpUseSsl ? 'ssl' : 'tls'}
                  onChange={e => setForm(p => ({ ...p, smtpUseSsl: e.target.value === 'ssl' }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                >
                  <option value="ssl">SSL (Port 465)</option>
                  <option value="tls">TLS/STARTTLS (Port 587)</option>
                </select>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700">
              <strong>Note:</strong> Customers will receive review request emails 45 minutes after their order is completed (delivered/ready for collection).
            </div>
        </fieldset>
      </div>

      {/* Banner Settings */}
      <div className="settings-panel settings-appearance bg-white rounded-2xl border border-gray-200 p-5">
        <div className="mb-4"><h3 className="font-semibold text-gray-900">Promotional banner</h3><p className="text-xs text-gray-500 mt-1">Update the large campaign image and its supporting message.</p></div>
        <div className="admin-banner-editor">
          <div className="admin-banner-fields">
            <div>
            <label className="text-xs text-gray-500 mb-1 block">Banner Image URL</label>
            <input type="url" placeholder="https://..." value={form.banner}
              onChange={e => setForm(p => ({ ...p, banner: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
            </div>
            <div>
            <label className="text-xs text-gray-500 mb-1 block">Banner Text</label>
            <input type="text" placeholder="e.g. BUY ONE GET ONE HALF PRICE AVAILABLE" value={form.bannerText}
              onChange={e => setForm(p => ({ ...p, bannerText: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
            <p className="text-xs text-gray-400 mt-1">This text appears on the banner when BOGO offers are active</p>
            </div>
          </div>
          <div className="admin-banner-preview">
            {form.banner ? <img src={form.banner} alt="Banner preview" onError={e => { e.target.style.display = 'none'; }} /> : <div>No banner image</div>}
          </div>
        </div>
      </div>

      {/* Domain & Deployment Settings */}
      <div className="settings-panel settings-advanced bg-white rounded-2xl border border-gray-200 p-5">
        <div className="admin-section-heading mb-4">
          <div><h3 className="font-semibold text-gray-900">Domain & Deployment</h3>
          <p className="text-xs text-gray-500 mt-1">Set your custom domain for emails and future exports</p>
          </div>
        </div>
        <fieldset disabled className="admin-locked-fields">
          <label className="text-xs text-gray-500 mb-1 block">Custom Domain</label>
          <input 
            type="url" 
            placeholder="https://yourdomain.com or yourdomain.com"
            value={form.domain} 
            onChange={e => setForm(p => ({ ...p, domain: e.target.value }))}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" 
          />
          <p className="text-xs text-gray-400 mt-1">
            Used in review request emails and order confirmation emails. Leave empty to use default preview URL.
          </p>
        </fieldset>
      </div>

      {/* SEO Settings */}
<div className="settings-panel settings-appearance bg-white rounded-2xl border border-gray-200 p-5">
  <div className="mb-4">
    <h3 className="font-semibold text-gray-900">SEO Settings</h3>
    <p className="text-xs text-gray-500 mt-1">
      Improve Google search visibility and social sharing previews
    </p>
  </div>

  <div className="admin-seo-grid">
    <div>
      <label className="text-xs text-gray-500 mb-1 block">
        SEO Title
      </label>
      <input
        type="text"
        value={form.seoTitle}
        onChange={e =>
          setForm(p => ({ ...p, seoTitle: e.target.value }))
        }
        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm"
      />
    </div>

    <div className="admin-seo-wide">
      <label className="text-xs text-gray-500 mb-1 block">
        Meta Description
      </label>
      <textarea
        rows={3}
        value={form.metaDescription}
        onChange={e =>
          setForm(p => ({ ...p, metaDescription: e.target.value }))
        }
        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm"
      />
    </div>

    <div>
      <label className="text-xs text-gray-500 mb-1 block">
        Open Graph Title
      </label>
      <input
        type="text"
        value={form.ogTitle}
        onChange={e =>
          setForm(p => ({ ...p, ogTitle: e.target.value }))
        }
        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm"
      />
    </div>

    <div>
      <label className="text-xs text-gray-500 mb-1 block">
        Open Graph Image URL
      </label>
      <input
        type="url"
        value={form.ogImage}
        onChange={e =>
          setForm(p => ({ ...p, ogImage: e.target.value }))
        }
        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm"
      />
    </div>

    <div>
  <label className="text-xs text-gray-500 mb-1.5 block">
    Favicon URL
  </label>
  <input
    type="url"
    placeholder="https://example.com/favicon.png"
    value={form.faviconUrl}
    onChange={e =>
      setForm(p => ({ ...p, faviconUrl: e.target.value }))
    }
    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
  />

  {form.faviconUrl && (
    <div className="mt-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
      <p className="text-xs text-gray-600 mb-2">Preview:</p>
      <img
        src={form.faviconUrl}
        alt="Favicon preview"
        className="w-8 h-8"
        onError={e => { e.target.style.display = 'none'; }}
      />
    </div>
  )}
</div>

  </div>
</div>

      {/* Stripe Settings */}
      <div className="settings-panel settings-payments bg-white rounded-2xl border border-gray-200 p-5">
        <div className="admin-section-heading mb-4">
          <div><h3 className="font-semibold text-gray-900">Stripe Payment Settings</h3>
          <p className="text-xs text-gray-500 mt-1">Configure your Stripe API keys for payment processing</p>
          </div>
        </div>
        <fieldset disabled className="admin-locked-fields space-y-4">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Publishable Key</label>
            <input 
              type="text" 
              placeholder="pk_live_... or pk_test_..."
              value={form.stripePublishableKey} 
              onChange={e => setForm(p => ({ ...p, stripePublishableKey: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 font-mono" 
            />
            <p className="text-xs text-gray-400 mt-1">Used on the frontend for Stripe Checkout</p>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Secret Key</label>
            <input 
              type="password" 
              placeholder={restaurant?.stripeSecretKeyConfigured ? 'Configured — enter a new key to replace it' : 'sk_live_... or sk_test_...'}
              value={form.stripeSecretKey} 
              onChange={e => setForm(p => ({ ...p, stripeSecretKey: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 font-mono" 
            />
            <p className="text-xs text-gray-400 mt-1">Write-only. Leave blank to keep the currently configured backend key.</p>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Webhook Signing Secret</label>
            <input
              type="password"
              placeholder={restaurant?.stripeWebhookSecretConfigured ? 'Configured — enter a new secret to replace it' : 'whsec_...'}
              value={form.stripeWebhookSecret}
              onChange={e => setForm(p => ({ ...p, stripeWebhookSecret: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 font-mono"
            />
            <p className="text-xs text-gray-400 mt-1">Unique to this tenant's Stripe webhook destination. Leave blank to keep it unchanged.</p>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Webhook Endpoint</label>
            <div className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50 text-gray-700 font-mono break-all">
              {`${window.location.origin}/api/payments/webhook`}
            </div>
            <p className="text-xs text-gray-400 mt-1">Create a separate Stripe destination for this exact tenant URL.</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700">
            <strong>Note:</strong> Get your Stripe API keys from <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline">dashboard.stripe.com/apikeys</a>. Use test keys for development and live keys for production.
          </div>
        </fieldset>
      </div>
    </div>
  );
}
